<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <deleteConfirmationModal
            v-if="showDeleteConfirmationModal"
            :delete-function="deleteVerification"
            title="Delete verification"
            message="Are you sure you want to delete this verification?"
            @close-delete-confirmation-modal="showDeleteConfirmationModal = false"
            @refresh-records="getVerifications()"
          />
          <verificationDetailModal
            v-if="showDetailModal && selectedVerification"
            :verification="selectedVerification"
            @close-detail-modal="showDetailModal = false"
            @refresh-verifications="getVerifications()"
          />
          <verifyModal
            v-if="showVerifyModal"
            @close-verify-modal="showVerifyModal = false"
            @verified="getVerifications()"
          />
          <scanReceiptModal
            v-if="showScanModal"
            @close-scan-modal="showScanModal = false"
            @verified="getVerifications()"
          />

          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Verifications</h2>
                <p class="list-page__count">{{ countLabel }}</p>
              </div>
              <div class="list-page__head-actions">
                <button type="button" class="list-page__action is-secondary" v-on:click="showVerifyModal = true">
                  <i class="pi pi-bolt"></i>
                  <span>Verify transaction</span>
                </button>
                <button type="button" class="list-page__action" v-on:click="showScanModal = true">
                  <i class="pi pi-camera"></i>
                  <span>Scan receipt</span>
                </button>
              </div>
            </header>

            <!-- Status segmented filter (server-side) -->
            <div class="status-tabs">
              <button
                v-for="tab in statusTabs"
                :key="tab.value"
                type="button"
                :class="['status-tab', { 'is-active': statusFilter === tab.value }]"
                v-on:click="onStatusFilter(tab.value)"
              >
                {{ tab.label }}
              </button>
            </div>

            <!-- Toolbar: search (client) + bank + date range (server) -->
            <div class="list-page__toolbar">
              <div class="list-search">
                <i class="pi pi-search"></i>
                <input
                  v-model="searchTerm"
                  type="text"
                  placeholder="Search reference, account, amount…"
                />
              </div>
              <div class="list-page__toolbar-right">
                <select class="filter-select" v-model="bankFilter" v-on:change="onFilterChange">
                  <option value="">All banks</option>
                  <option v-for="bank in banks" :key="bank.value" :value="bank.value">{{ bank.label }}</option>
                </select>
                <div class="date-range">
                  <input type="date" class="filter-date" v-model="startDate" v-on:change="onFilterChange" aria-label="From date" />
                  <span class="date-sep">–</span>
                  <input type="date" class="filter-date" v-model="endDate" v-on:change="onFilterChange" aria-label="To date" />
                </div>
                <button v-if="hasActiveFilters" type="button" class="list-clear-btn" v-on:click="clearFilters">
                  <i class="pi pi-times"></i>
                  Clear
                </button>
              </div>
            </div>

            <div class="list-page__table">
              <tableLoader v-if="verificationsIsLoading" message="Loading verifications…" />
              <div v-if="!verificationsIsLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Bank</th>
                      <th scope="col">Reference</th>
                      <th scope="col">Payer</th>
                      <th scope="col">Date</th>
                      <th scope="col">Status</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(verification, index) in filteredVerifications"
                      v-bind:key="verification.id || index"
                    >
                      <th scope="row">{{ index + 1 }}</th>
                      <td class="row-name">{{ formatAmount(verification) }}</td>
                      <td><span class="badge badge-secondary">{{ bankLabel(verification.bank) }}</span></td>
                      <td>
                        <span class="ref-cell">{{ verification.transactionReference || verification.transactionId || "—" }}</span>
                      </td>
                      <td>
                        <div class="payer">
                          <span class="payer-name">{{ verification.accountName || "—" }}</span>
                          <span v-if="verification.accountNumber" class="payer-acct">{{ verification.accountNumber }}</span>
                        </div>
                      </td>
                      <td>{{ $filters.getDateAndTime(verification.date) }}</td>
                      <td><span class="badge" :class="statusBadgeClass(verification.status)">{{ statusLabel(verification.status) }}</span></td>
                      <td class="col-actions">
                        <div class="dropdown dropleft">
                          <div class="dropdown-toggle actions-dropdown" type="button" data-toggle="dropdown">
                            <i class="pi pi-ellipsis-h"></i>
                          </div>
                          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                            <a class="dropdown-item" v-on:click="viewVerification(verification)">View details</a>
                            <a
                              v-if="verification.status !== 'verified'"
                              class="dropdown-item"
                              v-on:click="quickUpdate(verification, 'verified')"
                            >Approve</a>
                            <a
                              v-if="verification.status !== 'rejected'"
                              class="dropdown-item"
                              v-on:click="quickUpdate(verification, 'rejected')"
                            >Reject</a>
                            <a class="dropdown-item final-item text-danger" v-on:click="toggleDeleteConfirmationModal(verification)">Delete</a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                v-if="!verificationsIsLoading && (!filteredVerifications || filteredVerifications.length === 0)"
                class="list-page__empty"
              >
                <i class="pi pi-shield"></i>
                <div v-if="searchTerm">No verifications match your search.</div>
                <div v-else>No verifications found.</div>
              </div>
            </div>

            <Pagination
              :metaData="verificationsMetaData"
              v-model:currentPage="currentPage"
              v-model:perPage="perPage"
              v-on:change="onPaginationChange"
            />
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import Pagination from "@/components/Pagination.vue";
import tableLoader from "@/components/Table_Loader.vue";
import deleteConfirmationModal from "@/components/Delete_Confirmation_Modal.vue";
import verificationDetailModal from "@/components/verification/Verification_Detail_Modal.vue";
import verifyModal from "@/components/verification/Verify_Modal.vue";
import scanReceiptModal from "@/components/verification/Scan_Receipt_Modal.vue";
import {
  DELETE_VERIFICATION,
  GET_VERIFICATIONS,
  UPDATE_VERIFICATION_STATUS,
} from "@/store/action_types";
import { BANK_LABELS, BANKS, VERIFICATION_STATUS } from "@/utils/enums";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";

const STATUS_BADGE = {
  [VERIFICATION_STATUS.verified]: "badge-success",
  [VERIFICATION_STATUS.pending]: "badge-warning",
  [VERIFICATION_STATUS.rejected]: "badge-danger",
  [VERIFICATION_STATUS.failed]: "badge-danger",
};

export default {
  title: "Verifications",
  components: {
    sidenav,
    navbar,
    Pagination,
    tableLoader,
    deleteConfirmationModal,
    verificationDetailModal,
    verifyModal,
    scanReceiptModal,
  },
  data() {
    return {
      statusTabs: [
        { label: "All", value: "" },
        { label: "Verified", value: VERIFICATION_STATUS.verified },
        { label: "Failed", value: VERIFICATION_STATUS.failed },
      ],
      banks: [
        { label: BANK_LABELS[BANKS.cbe], value: BANKS.cbe },
        { label: BANK_LABELS[BANKS.telebirr], value: BANKS.telebirr },
        { label: BANK_LABELS[BANKS.boa], value: BANKS.boa },
      ],
      statusFilter: "",
      bankFilter: "",
      startDate: "",
      endDate: "",
      searchTerm: "",
      currentPage: 1,
      perPage: 30,
      selectedVerification: null,
      showDetailModal: false,
      showDeleteConfirmationModal: false,
      showVerifyModal: false,
      showScanModal: false,
    };
  },
  computed: {
    verifications() {
      return this.$store.getters.verifications.data;
    },
    verificationsIsLoading() {
      return this.$store.getters.verifications.isLoading;
    },
    verificationsMetaData() {
      return this.$store.getters.verifications.metaData;
    },
    // Client-side text filter over the loaded page (the API has no free-text search).
    filteredVerifications() {
      const list = this.verifications || [];
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return list;
      return list.filter((v) => {
        const haystack = [
          v.transactionReference,
          v.transactionId,
          v.accountName,
          v.accountNumber,
          v.currency,
          v.amount,
          this.bankLabel(v.bank),
        ]
          .filter((value) => value != null)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    hasActiveFilters() {
      return !!(this.bankFilter || this.startDate || this.endDate || this.searchTerm || this.statusFilter);
    },
    countLabel() {
      const total = this.verificationsMetaData?.totalRecords ?? (this.verifications || []).length;
      return total === 1 ? "1 verification" : `${total} verifications`;
    },
  },
  methods: {
    bankLabel(bank) {
      return BANK_LABELS[bank] || String(bank || "").toUpperCase();
    },
    statusLabel(status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
    },
    statusBadgeClass(status) {
      return STATUS_BADGE[status] || "badge-secondary";
    },
    formatAmount(verification) {
      const amount = Number(verification.amount);
      if (isNaN(amount)) return "—";
      return this.$filters.formatPrice(amount, verification.currency || null);
    },
    onStatusFilter(status) {
      this.statusFilter = status;
      this.currentPage = 1;
      this.getVerifications();
    },
    onFilterChange() {
      this.currentPage = 1;
      this.getVerifications();
    },
    clearFilters() {
      this.statusFilter = "";
      this.bankFilter = "";
      this.startDate = "";
      this.endDate = "";
      this.searchTerm = "";
      this.currentPage = 1;
      this.getVerifications();
    },
    onPaginationChange({ page, perPage }) {
      this.currentPage = page;
      this.perPage = perPage;
      this.getVerifications();
    },
    viewVerification(verification) {
      this.selectedVerification = verification;
      this.showDetailModal = true;
    },
    quickUpdate(verification, status) {
      this.$store
        .dispatch(UPDATE_VERIFICATION_STATUS, { id: verification.id, status })
        .then((res) => {
          if (res) {
            this.$toast.success(
              status === "verified" ? "Verification approved." : "Verification rejected.",
              TOAST_GLOBAL_CONFIG,
            );
          }
        });
    },
    toggleDeleteConfirmationModal(verification) {
      this.selectedVerification = verification;
      this.showDeleteConfirmationModal = true;
    },
    deleteVerification() {
      return this.$store.dispatch(DELETE_VERIFICATION, this.selectedVerification.id);
    },
    getVerifications() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
      };
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.bankFilter) params.bank = this.bankFilter;
      // The backend filters by date only when both ends are provided.
      if (this.startDate && this.endDate) {
        params.startDate = this.startDate;
        params.endDate = this.endDate;
      }
      this.$store.dispatch(GET_VERIFICATIONS, params);
    },
  },
  mounted() {
    this.getVerifications();
  },
};
</script>
<style scoped>
.status-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  background: var(--ehs-color-canvas-strong);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-pill);
  width: fit-content;
}

.status-tab {
  border: 0;
  background: transparent;
  padding: 7px 16px;
  border-radius: var(--ehs-radius-pill);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--ehs-color-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.status-tab:hover {
  color: var(--ehs-color-ink);
}

.status-tab.is-active {
  background: var(--ehs-color-surface);
  color: var(--ehs-color-primary);
  box-shadow: var(--ehs-shadow-xs);
}

.filter-select,
.filter-date {
  height: 36px !important;
  padding: 0 12px !important;
  background: var(--ehs-color-surface);
  border: 1px solid var(--ehs-color-border);
  border-radius: var(--ehs-radius-md);
  color: var(--ehs-color-ink);
  font-size: 0.78rem;
  font-family: inherit;
  outline: none;
  box-shadow: var(--ehs-shadow-xs);
}

.filter-select:focus,
.filter-date:focus {
  border-color: var(--ehs-color-primary);
  box-shadow: var(--ehs-shadow-focus);
}

.date-range {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.date-sep {
  color: var(--ehs-color-soft);
}

.ref-cell {
  font-family: var(--ehs-font-mono);
  font-size: 0.78rem;
  color: var(--ehs-color-text);
}

.payer {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.payer-name {
  color: var(--ehs-color-ink);
  font-weight: 500;
}

.payer-acct {
  font-family: var(--ehs-font-mono);
  font-size: 0.72rem;
  color: var(--ehs-color-soft);
}

.list-page__head-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* "Verify transaction" sits next to the primary green "Scan receipt" as a
   neutral secondary action. */
.list-page__action.is-secondary {
  background: var(--ehs-color-surface);
  border-color: var(--ehs-color-border);
  color: var(--ehs-color-ink);
}

.list-page__action.is-secondary:hover {
  background: var(--ehs-color-canvas);
  border-color: var(--ehs-color-border-strong);
  color: var(--ehs-color-ink);
}

@media (max-width: 991.98px) {
  .list-page__head-actions {
    width: 100%;
  }

  .list-page__head-actions .list-page__action {
    flex: 1 1 0;
    justify-content: center;
  }

  .status-tabs {
    width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
  }

  /* This toolbar carries more controls than the shared list pattern assumes
     (search + bank select + a two-input date range + clear), so the global
     "keep it on one row" rule crams them unusably tight on a phone. Let the
     search take its own row and the filters wrap onto the next. */
  .list-page__toolbar {
    flex-wrap: wrap;
  }

  .list-search {
    flex: 1 1 100%;
  }

  .list-page__toolbar-right {
    flex: 1 1 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .filter-select {
    flex: 1 1 140px;
  }

  .date-range {
    flex: 1 1 100%;
  }

  .filter-date {
    min-width: 0;
    flex: 1 1 auto;
  }
}
</style>
